import { computed, isRef, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'

import type { CampaignFormData } from '@/components/ReviewsManagement/Reviews/types'
import {
  IncentivizationCampaignIncentivizationTypeEnum,
  type ProductData,
  type IncentivizationCampaignPersonalizationLocaleEnum,
} from '@/gql/reviews'

export const TEXT_MIN_LENGTH = 10
export const TEXT_MAX_LENGTH = 250

const TYPE_SPECIFIC_INVITE_TEXT_KEYS: Partial<Record<IncentivizationCampaignIncentivizationTypeEnum, string>> = {
  [IncentivizationCampaignIncentivizationTypeEnum.Either]: 'Reviews.product.campaign.form.inviteMessage.input.extendedTextEither',
  [IncentivizationCampaignIncentivizationTypeEnum.Donation]: 'Reviews.product.campaign.form.inviteMessage.input.extendedTextDonation',
}

export function useReviewsCampaignPersonalizationFormOptions(
  campaign: Ref<Pick<CampaignFormData, 'incentivizationType' | 'amount'>> | Pick<CampaignFormData, 'incentivizationType' | 'amount'>,
  product: Ref<ProductData> | ProductData,
  locale: Ref<IncentivizationCampaignPersonalizationLocaleEnum> | IncentivizationCampaignPersonalizationLocaleEnum,
) {
  const { t } = useI18n()

  const campaignValue = computed(() => {
    return isRef(campaign) ? campaign.value : campaign
  })

  const productValue = computed(() => {
    return isRef(product) ? product.value : product
  })

  const localeValue = computed<IncentivizationCampaignPersonalizationLocaleEnum>(() => {
    return isRef(locale) ? locale.value : locale
  })

  function getDefaultInviteMessage() {
    const campaignData = campaignValue.value
    const productData = productValue.value
    const currentLocale = localeValue.value

    const { amount, incentivizationType } = campaignData

    if (amount && incentivizationType) {
      const typeSpecificTextKey = TYPE_SPECIFIC_INVITE_TEXT_KEYS[incentivizationType]
      if (typeSpecificTextKey) {
        return t(typeSpecificTextKey, { productName: productData.title, amount }, { locale: currentLocale })
      }

      const incentivizationTypeLabel = t(
        `Reviews.product.campaign.form.incentivizationType.input.option.${incentivizationType}`,
        {},
        {
          locale: currentLocale,
        },
      )

      return t(
        'Reviews.product.campaign.form.inviteMessage.input.extendedText',
        {
          productName: productData.title,
          incentivizationType: incentivizationTypeLabel,
          amount,
        },
        { locale: currentLocale },
      )
    }
    return t('Reviews.product.campaign.form.inviteMessage.input.defaultText', { productName: productData.title }, { locale: currentLocale })
  }

  return {
    TEXT_MIN_LENGTH,
    TEXT_MAX_LENGTH,
    getDefaultInviteMessage,
  }
}
